import { ButtonColor } from "@/common/enum/button-color";
import { AdminItemPath } from "@/common/enum/path";
import { ItemDetailDto } from "@/types/dto/item-detail-dto";
import { Button, Modal } from "flowbite-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { ImageCarouselViewer } from "../carousel/ImageCarouselViewer";
import { EditOutlineIcon } from "../icon/EditOutlineIcon";
import { Body14Medium } from "../typography/Body14Medium";
import { Body14Regular } from "../typography/Body14Regular";
import { Footnote12Description } from "../typography/Footnote12Description";
import { H3 } from "../typography/H3";
import { H4 } from "../typography/H4";
import { H5 } from "../typography/H5";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";

interface Props {
  selectedItem: ItemDetailDto;
  clearSelectedItem: () => void;
}

const ItemDetailModal: React.FC<Props> = ({
  selectedItem,
  clearSelectedItem,
}) => {
  const navigate = useNavigate();

  const detailsEditor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: true,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: {
          class: "custom-link",
        },
      }),
      Underline,
      Image.configure({
        HTMLAttributes: {
          class: "custom-image",
        },
      }),
    ],
    content: selectedItem.details ?
      (function () {
        try {
          return JSON.parse(selectedItem.details);
        } catch {
          return selectedItem.details;
        }
      })() : {},
    editable: false,
  });

  const featuresEditor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: true,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: {
          class: "custom-link",
        },
      }),
      Underline,
      Image.configure({
        HTMLAttributes: {
          class: "custom-image",
        },
      }),
    ],
    content: selectedItem.features ?
      (function () {
        try {
          return JSON.parse(selectedItem.features);
        } catch {
          return selectedItem.features;
        }
      })() : {},
    editable: false,
  });

  const handleUpdateItemButtonClick = () => {
    navigate(
      `${AdminItemPath.SINGLE_PRODUCT}/${selectedItem.id}${AdminItemPath.SINGLE_PRODUCT_EDIT}`
    );
  };

  return (
    <Modal size="7xl" show={true} onClose={clearSelectedItem}>
      <Modal.Header>
        <H4 className="text-semantic-text-heading">상품 상세 정보</H4>
      </Modal.Header>
      <Modal.Body>
        <div className="flex">
          <ImageCarouselViewer images={selectedItem.imageUrls} />
          <div className="flex flex-col ml-[20px] max-w-[412px] whitespace-break-spaces">
            <Footnote12Description className="text-semantic-text-tertiary mb-[4px] max-w-[412px] whitespace-break-spaces">
              {selectedItem.category.name}
            </Footnote12Description>
            <div className="flex mb-[16px]">
              <H3 className="text-semantic-text-heading max-w-[412px] whitespace-break-spaces">
                {selectedItem.name}
              </H3>
              <Footnote12Description className="text-semantic-text-tertiary ml-[4px]">
                {/* model name */}
                {selectedItem.modelName}
              </Footnote12Description>
            </div>
            <div className="text-semantic-text-body mb-[16px] max-w-[412px] whitespace-break-spaces">
              <EditorContent
                editor={detailsEditor}
                className="[&_.ProseMirror]:!min-h-0 [&_.ProseMirror]:!h-auto"
              />
            </div>
            <H5 className="text-semantic-text-body mb-[8px]">기능</H5>
            <div className="max-w-[412px] whitespace-break-spaces">
              <EditorContent
                editor={featuresEditor}
                className="[&_.ProseMirror]:!min-h-0 [&_.ProseMirror]:!h-auto"
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          color={ButtonColor.PRIMARY_OUTLINE}
          onClick={handleUpdateItemButtonClick}
        >
          <EditOutlineIcon size={20} color="text-grayscale-contrast-80" />
          <Body14Medium className="text-semantic-text-body ml-[8px]">
            제품 정보 수정하기
          </Body14Medium>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ItemDetailModal;
